// useVoiceOutput.ts — React hook for Anna's voice output (TTS)
//
// Queues text for TTS playback, manages audio state, and provides
// interrupt capability. When the user starts speaking (voice input),
// Anna's audio is interrupted — natural conversation turn-taking.
//
// Anna ALWAYS responds with text in UI. Voice output is an
// optional overlay that reads the text aloud.

import { useState, useCallback, useRef, useEffect } from 'react';
import { speakText, type TTSPlayback } from '@services/ttsService';

interface UseVoiceOutputOptions {
  /** Whether TTS is enabled (user preference) */
  enabled: boolean;
  /** Interrupt playback when user starts talking */
  interruptOnUserSpeech?: boolean;
}

interface UseVoiceOutputReturn {
  /** Whether Anna's audio is currently playing */
  isSpeaking: boolean;
  /** Queue text for TTS playback */
  speak: (text: string) => void;
  /** Immediately stop all playback */
  stop: () => void;
  /** TTS enabled state (reflects options.enabled) */
  enabled: boolean;
}

export function useVoiceOutput(options: UseVoiceOutputOptions): UseVoiceOutputReturn {
  const { enabled } = options;
  const [isSpeaking, setIsSpeaking] = useState(false);

  const playbackRef = useRef<TTSPlayback | null>(null);
  const queueRef = useRef<string[]>([]);
  const processingRef = useRef(false);

  const stop = useCallback(() => {
    queueRef.current = [];
    if (playbackRef.current?.isPlaying()) {
      playbackRef.current.stop();
    }
    playbackRef.current = null;
    processingRef.current = false;
    setIsSpeaking(false);
  }, []);

  // Process queue: speak one chunk at a time
  const processQueue = useCallback(async () => {
    if (processingRef.current || !enabled) return;
    processingRef.current = true;

    while (queueRef.current.length > 0) {
      const text = queueRef.current.shift()!;
      if (!text.trim()) continue;

      setIsSpeaking(true);
      try {
        const playback = await speakText({ text });
        playbackRef.current = playback;
        await playback.done;
      } catch (err) {
        console.warn('[useVoiceOutput] TTS error:', err);
      }
      playbackRef.current = null;
    }

    setIsSpeaking(false);
    processingRef.current = false;
  }, [enabled]);

  const speak = useCallback((text: string) => {
    if (!enabled) return;
    queueRef.current.push(text);
    processQueue();
  }, [enabled, processQueue]);

  // Cleanup on unmount
  useEffect(() => stop, [stop]);

  return { isSpeaking, speak, stop, enabled };
}
